import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary, Card } from '@kollez/design-system';
import { useWorkflowRunsList, type WorkflowRunListParams, type WorkflowRunRow } from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';

/**
 * /workflows/runs — the tenant-wide WorkflowRun index (Ship 5e).
 *
 * The Ship 5e schema (Workflow + WorkflowRun + Badge) had a single-run
 * view on ApplicationDetail's sidebar but no way to see all live runs at
 * once. This page consumes GET /workflow-runs with status + stage filters
 * so TPO/HoD can see funnel state across drives — who's stuck at which
 * stage, which auto-satisfied via a Badge, which are flagged for review.
 */

const STATUS_TONE: Record<WorkflowRunRow['status'], string> = {
  active: 'badge-info',
  completed: 'badge-success',
  abandoned: '',
  terminated: 'badge-danger',
  flagged: 'badge-warning',
};

const STATUSES: Array<WorkflowRunRow['status']> = ['active', 'completed', 'abandoned', 'terminated', 'flagged'];

export function WorkflowRunsPage() {
  const [status, setStatus] = useState<WorkflowRunRow['status'] | ''>('');
  const [currentStage, setCurrentStage] = useState('');
  const params: WorkflowRunListParams = { limit: 100, sortBy: 'updatedAt', sortDir: 'desc' };
  if (status) params.status = status;
  if (currentStage.trim()) params.currentStage = currentStage.trim();
  const q = useWorkflowRunsList(params);
  const rows = q.data?.runs ?? [];
  const summary = q.data?.summary?.byStatus ?? {};

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Workflows · Runs</p>
          <h1 className="h-page">Workflow runs</h1>
          <p className="subhead">
            Live view of every student's stage across every workflow. Filter by status or stage
            to find who's stuck, who auto-satisfied via a badge, and who needs review.
          </p>
        </div>
        <div className="ph-actions">
          <Link to="/workflows" className="btn btn-outline">Workflow library</Link>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-5">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={`card card-pad text-left ${status === s ? 'ring-2 ring-ink-accent' : ''}`}
            onClick={() => setStatus(status === s ? '' : s)}
          >
            <div className="cell-sub capitalize">{s}</div>
            <div className="h-section">{summary[s] ?? 0}</div>
          </button>
        ))}
      </div>

      <Card padding="md">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-sm">
            <span className="cell-sub">Filter by stage</span>
            <input
              className="input mt-1"
              placeholder="e.g. interview, offer, shortlist"
              value={currentStage}
              onChange={(e) => setCurrentStage(e.target.value)}
            />
          </label>
          {(status || currentStage) && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { setStatus(''); setCurrentStage(''); }}
            >
              Clear filters
            </button>
          )}
          <div className="ml-auto cell-sub">
            {q.data?.meta ? `${rows.length} of ${q.data.meta.total} shown` : `${rows.length} runs`}
          </div>
        </div>
      </Card>

      <Card padding="none">
        <AsyncBoundary
          kind="table"
          inCard
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && rows.length === 0}
          emptyTitle={status || currentStage ? 'No matching runs' : 'No workflow runs yet'}
          emptyHint={status || currentStage
            ? 'Adjust filters or clear them to see every run.'
            : 'Runs are created when an application enters a workflow-controlled drive.'}
          onRetry={() => void q.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th className="hide-mobile">Auto-satisfied</th>
                  <th className="r">Since</th>
                  <th className="r">Application</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const wf = typeof r.workflow === 'object' ? r.workflow : null;
                  const app = typeof r.application === 'object' ? r.application : null;
                  const appId = app?._id ?? (typeof r.application === 'string' ? r.application : '');
                  return (
                    <tr key={r._id}>
                      <td>
                        <div className="cell-title truncate">{wf?.name ?? 'Workflow'}</div>
                        {wf?.version !== undefined && (
                          <div className="cell-sub">v{wf.version}</div>
                        )}
                      </td>
                      <td><span className="badge">{r.currentStage}</span></td>
                      <td><span className={`badge ${STATUS_TONE[r.status]}`}>{r.status}</span></td>
                      <td className="hide-mobile">
                        {r.autoSatisfiedStages.length > 0 ? (
                          <span className="badge badge-info">{r.autoSatisfiedStages.length} auto</span>
                        ) : (
                          <span className="cell-sub">—</span>
                        )}
                      </td>
                      <td className="r cell-sub">
                        {formatIndianDate(new Date(r.enteredCurrentStageAt))} {formatTime(r.enteredCurrentStageAt)}
                      </td>
                      <td className="r">
                        {appId ? (
                          <Link to={`/applications/${appId}`} className="link">Open</Link>
                        ) : (
                          <span className="cell-sub">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </Card>
    </div>
  );
}
